export interface TppFileBlob {
  mimeType: string;
  byteSize: number;
  data: ArrayBuffer;
}

export interface TppSettings {
  poolKey: string;
  displayPrefix: string;
  displaySuffix: string;
  isoCurrencyCode: string | null;
  fractionDigits: number;
  inputRoundingMode: string;
  displayRoundingMode: string;
}

export interface TppMeta {
  revision: number;
  updatedAt: string;
  schemaVersion: 1;
}

export interface TppCardRow {
  id: number;
  poolKey: string;
  displayNumber: number;
  physicalId: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface TppEventRow {
  id: number;
  cardId: number;
  eventKind: 'usage' | 'charge';
  outDate: string;
  inDate: string | null;
  departmentLabel: string;
  borrowerName: string;
  purpose: string;
  place: string;
  route: string;
  amount: number | null;
  checkoutStatus: 0 | 1;
  proofStoredFilename: string | null;
  proofMime: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface TppSnapshot {
  version: 1;
  settings: TppSettings;
  meta: TppMeta;
  cards: TppCardRow[];
  events: TppEventRow[];
  files: Record<string, TppFileBlob>;
  counters: { card: number; event: number };
}

export function defaultTppSettings(poolKey = 'default'): TppSettings {
  return {
    poolKey,
    displayPrefix: '',
    displaySuffix: '円',
    isoCurrencyCode: null,
    fractionDigits: 0,
    inputRoundingMode: 'none',
    displayRoundingMode: 'none',
  };
}

export function defaultTppMeta(): TppMeta {
  return { revision: 0, updatedAt: nowIso(), schemaVersion: 1 };
}

/** 返却日と checkoutStatus の食い違いを直し、カードあたり未返却は最新1件に揃える */
function normalizeTppEvents(events: TppEventRow[]): TppEventRow[] {
  const repaired: TppEventRow[] = events.map((e) => {
    const eventKind: TppEventRow['eventKind'] = e.eventKind === 'charge' ? 'charge' : 'usage';
    const inDate = e.inDate == null || String(e.inDate).trim() === '' ? null : e.inDate;
    let checkoutStatus: 0 | 1 = e.checkoutStatus === 1 ? 1 : 0;
    if (eventKind === 'charge') {
      checkoutStatus = 0;
    } else if (inDate == null) {
      checkoutStatus = 1;
    } else {
      checkoutStatus = 0;
    }
    // 残高 = -Σamount。チャージは負・利用は非負に揃える（Excel 取込や手編集の符号ずれ対策）
    let amount = e.amount;
    if (amount != null && Number.isFinite(amount) && amount !== 0) {
      if (eventKind === 'charge') amount = -Math.abs(amount);
      else amount = Math.abs(amount);
    }
    return {
      ...e,
      eventKind,
      inDate,
      checkoutStatus,
      amount,
      proofStoredFilename: e.proofStoredFilename ?? null,
      proofMime: e.proofMime ?? null,
      departmentLabel: e.departmentLabel ?? '',
      borrowerName: e.borrowerName ?? '',
      purpose: e.purpose ?? '',
      place: e.place ?? '',
      route: e.route ?? '',
    };
  });

  const openIdByCard = new Map<number, number>();
  for (const e of repaired) {
    if (e.checkoutStatus !== 1) continue;
    const prev = openIdByCard.get(e.cardId);
    if (prev == null || e.id > prev) openIdByCard.set(e.cardId, e.id);
  }

  return repaired.map((e) => {
    if (e.checkoutStatus !== 1) return e;
    if (openIdByCard.get(e.cardId) === e.id) return e;
    return { ...e, checkoutStatus: 0 };
  });
}

export function normalizeTppSnapshot(raw: Partial<TppSnapshot> | null | undefined): TppSnapshot {
  const poolKey = raw?.settings?.poolKey ?? 'default';
  const cards = raw?.cards ?? [];
  const events = normalizeTppEvents(raw?.events ?? []);
  const maxCardId = cards.reduce((m, c) => Math.max(m, c.id), 0);
  const maxEventId = events.reduce((m, e) => Math.max(m, e.id), 0);
  return {
    version: 1,
    settings: { ...defaultTppSettings(poolKey), ...(raw?.settings ?? {}) },
    meta: { ...defaultTppMeta(), ...(raw?.meta ?? {}), schemaVersion: 1 },
    cards,
    events,
    files: raw?.files ?? {},
    counters: {
      card: Math.max(raw?.counters?.card ?? 1, maxCardId + 1),
      event: Math.max(raw?.counters?.event ?? 1, maxEventId + 1),
    },
  };
}

export function emptySnapshot(poolKey = 'default'): TppSnapshot {
  return normalizeTppSnapshot({
    version: 1,
    settings: defaultTppSettings(poolKey),
    meta: defaultTppMeta(),
    cards: [],
    events: [],
    files: {},
    counters: { card: 1, event: 1 },
  });
}

export function bumpTppSnapshotMeta(snap: TppSnapshot): void {
  snap.meta.revision = (snap.meta.revision ?? 0) + 1;
  snap.meta.updatedAt = nowIso();
}

/** セッション中のみ保持（transit-pass-pool.zip から読み込んだ作業コピー） */
let sessionSnapshot: TppSnapshot | null = null;

export function isTppSnapshotLoaded(): boolean {
  return sessionSnapshot != null;
}

/** 同期 peek（次の表示番号など。未ロード時は null） */
export function peekTppSnapshot(): TppSnapshot | null {
  return sessionSnapshot;
}

/** ZIP 切断時にメモリ上の作業コピーを破棄 */
export function resetTppDbSession(): void {
  sessionSnapshot = null;
}

let snapshotWrittenHook: (() => void) | null = null;

/** transit-pass-pool.zip への自動同期。循環 import を避けるためコールバックで接続する。 */
export function setTppSnapshotWrittenHook(fn: (() => void) | null): void {
  snapshotWrittenHook = fn;
}

export async function readTppSnapshot(): Promise<TppSnapshot> {
  if (!sessionSnapshot) {
    throw new Error('tpp_zip_not_loaded');
  }
  return sessionSnapshot;
}

export async function writeTppSnapshot(
  snapshot: TppSnapshot,
  bumpMeta = true,
  options?: { notifySync?: boolean }
): Promise<void> {
  const normalized = normalizeTppSnapshot(snapshot);
  if (bumpMeta) bumpTppSnapshotMeta(normalized);
  sessionSnapshot = normalized;
  // フル読込（replace）ではフックを飛ばし、偽の未DL／都度同期を防ぐ
  if (options?.notifySync !== false) {
    snapshotWrittenHook?.();
  }
}

export async function mutateTppSnapshot(
  mutator: (snap: TppSnapshot) => void | Promise<void>
): Promise<TppSnapshot> {
  const snap = await readTppSnapshot();
  await mutator(snap);
  await writeTppSnapshot(snap);
  return snap;
}

export function nowIso(): string {
  return new Date().toISOString();
}

export function newStoredFilename(original: string): string {
  const ext = original.includes('.') ? original.slice(original.lastIndexOf('.')) : '';
  return `${crypto.randomUUID()}${ext}`;
}
